import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CabMeetingForm from './CabMeetingForm';
import UserLookupModal from '../../../components/lookup/UserLookupModal';
import {
  deleteCabMeeting,
  formDataToUpdateMeetingPayload,
  getCabMeetingById,
  recordToCabMeetingFormData,
  sendNewCabMeetingInvite,
  updateCabMeeting,
} from '../../../services/cabMeetingsService';
import { fetchCabAgendaItems } from '../../../services/cabAgendaItemsService';
import {
  addCabMeetingAttendees,
  fetchCabMeetingAttendees,
} from '../../../services/cabAttendeesService';
import { getAgendaStatusLabel } from '../../../constants/cabAgendaOptions';
import { CAB_EMAIL_POLICY_MESSAGE, isAllowedCabEmail } from '../../../utils/cabEmailPolicy';
import {
  CAB_ATTENDANCE_STATUS_OPTIONS,
  CAB_ATTENDEE_REASON_OPTIONS,
  getAttendanceStatusLabel,
  getAttendeeReasonLabel,
} from '../../../constants/cabAttendeeOptions';
import { changeStateLabels } from '../../../utils/labelMappings';
import type { CabMeetingFormData } from '../../../types/cabMeeting';
import type { LookupUser } from '../../../components/lookup/lookupTypes';
import {
  hasCabMeetingValidationErrors,
  validateCabMeetingForm,
  type CabMeetingFormErrors,
} from '../../../utils/cabMeetingValidation';

type ContentTab = 'conference' | 'notes';
type RelatedTab = 'agenda' | 'attendees';
type AttendeeSortKey = 'attendeeName' | 'reason' | 'attendanceStatus' | 'inviteSent';

function formatAgendaChangeState(changeState?: string | null): string {
  if (!changeState) return '—';
  return changeStateLabels[changeState] || changeState;
}

const CabMeetingDetail: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState<CabMeetingFormData | null>(null);
  const [errors, setErrors] = useState<CabMeetingFormErrors>({});
  const [contentTab, setContentTab] = useState<ContentTab>('conference');
  const tabParam = searchParams.get('tab');
  const relatedTab: RelatedTab = tabParam === 'attendees' ? 'attendees' : 'agenda';
  const [agendaSearchInput, setAgendaSearchInput] = useState('');
  const [agendaSearch, setAgendaSearch] = useState('');
  const [attendeeSearchInput, setAttendeeSearchInput] = useState('');
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [attendeeAttendanceFilter, setAttendeeAttendanceFilter] = useState('');
  const [attendeeReasonFilter, setAttendeeReasonFilter] = useState('');
  const [attendeePage, setAttendeePage] = useState(1);
  const [attendeeSortKey, setAttendeeSortKey] = useState<AttendeeSortKey>('attendeeName');
  const [attendeeSortDir, setAttendeeSortDir] = useState<'asc' | 'desc'>('asc');
  const [attendeeLookupOpen, setAttendeeLookupOpen] = useState(false);
  const [agendaPage, setAgendaPage] = useState(1);
  const agendaPageSize = 25;
  const attendeePageSize = 25;

  const setRelatedTab = (tab: RelatedTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const { data: record, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['cab-meeting', meetingId],
    queryFn: () => getCabMeetingById(meetingId!),
    enabled: Boolean(meetingId),
    retry: 1,
  });

  useEffect(() => {
    if (record) {
      setFormData(recordToCabMeetingFormData(record));
    }
  }, [record]);

  const {
    data: agendaData,
    isLoading: agendaLoading,
    isFetching: agendaFetching,
  } = useQuery({
    queryKey: ['cab-meeting-agenda', meetingId, agendaPage, agendaPageSize, agendaSearch],
    queryFn: () =>
      fetchCabAgendaItems(meetingId!, {
        page: agendaPage,
        pageSize: agendaPageSize,
        search: agendaSearch || undefined,
      }),
    enabled: Boolean(meetingId) && relatedTab === 'agenda',
  });

  const agendaItems = agendaData?.items ?? [];
  const agendaTotal = agendaData?.total ?? 0;
  const agendaTotalPages = Math.max(1, Math.ceil(agendaTotal / agendaPageSize));

  type AgendaSortKey =
    | 'sortOrder'
    | 'changeNumber'
    | 'shortDescription'
    | 'changeState'
    | 'assignmentGroupName'
    | 'assignedToName'
    | 'status';
  const [agendaSortKey, setAgendaSortKey] = useState<AgendaSortKey>('sortOrder');
  const [agendaSortDir, setAgendaSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setAgendaSortKey('sortOrder');
    setAgendaSortDir('asc');
  }, [meetingId]);

  const displayedAgendaItems = [...agendaItems].sort((a, b) => {
    if (agendaSortKey === 'sortOrder') {
      const orderCmp = a.sortOrder - b.sortOrder;
      if (orderCmp !== 0) return agendaSortDir === 'asc' ? orderCmp : -orderCmp;
      return a.createdAt.localeCompare(b.createdAt);
    }
    const aVal = String(
      agendaSortKey === 'changeState'
        ? formatAgendaChangeState(a.changeState)
        : a[agendaSortKey] ?? ''
    );
    const bVal = String(
      agendaSortKey === 'changeState'
        ? formatAgendaChangeState(b.changeState)
        : b[agendaSortKey] ?? ''
    );
    const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
    return agendaSortDir === 'asc' ? cmp : -cmp;
  });

  const toggleAgendaSort = (key: AgendaSortKey) => {
    if (agendaSortKey === key) {
      setAgendaSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setAgendaSortKey(key);
      setAgendaSortDir('asc');
    }
  };

  const {
    data: attendeesData,
    isLoading: attendeesLoading,
    isFetching: attendeesFetching,
  } = useQuery({
    queryKey: [
      'cab-meeting-attendees',
      meetingId,
      attendeePage,
      attendeePageSize,
      attendeeSearch,
      attendeeAttendanceFilter,
      attendeeReasonFilter,
      attendeeSortKey,
      attendeeSortDir,
    ],
    queryFn: () =>
      fetchCabMeetingAttendees(meetingId!, {
        page: attendeePage,
        pageSize: attendeePageSize,
        search: attendeeSearch || undefined,
        attendanceStatus: attendeeAttendanceFilter || undefined,
        reason: attendeeReasonFilter || undefined,
        sortBy: attendeeSortKey,
        sortDir: attendeeSortDir,
      }),
    enabled: Boolean(meetingId) && relatedTab === 'attendees',
  });

  const attendees = attendeesData?.items ?? [];
  const attendeesTotal = attendeesData?.total ?? 0;
  const attendeesTotalPages = Math.max(1, Math.ceil(attendeesTotal / attendeePageSize));

  useEffect(() => {
    setAttendeeSortKey('attendeeName');
    setAttendeeSortDir('asc');
    setAttendeePage(1);
  }, [meetingId]);

  const toggleAttendeeSort = (key: AttendeeSortKey) => {
    if (attendeeSortKey === key) {
      setAttendeeSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setAttendeeSortKey(key);
      setAttendeeSortDir('asc');
    }
  };

  const addAttendeesMutation = useMutation({
    mutationFn: (users: LookupUser[]) =>
      addCabMeetingAttendees(meetingId!, { userIds: users.map((u) => u.id) }),
    onSuccess: async (result) => {
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-attendees', meetingId] });

      if (result.items.length > 0) {
        toast.success(
          result.items.length === 1
            ? 'Attendee added successfully.'
            : `${result.items.length} attendees added successfully.`
        );
      }

      const warnings = result.warnings?.length
        ? result.warnings
        : result.message
          ? [result.message]
          : [];
      for (const warning of warnings) {
        const isInviteFailure = warning.toLowerCase().includes('invitation could not be sent');
        const isDuplicateSkip =
          warning.toLowerCase().includes('already attendees') ||
          warning.toLowerCase().includes('already part of this meeting');
        if (isInviteFailure) {
          toast.error(warning, { duration: 7000 });
        } else if (isDuplicateSkip && result.items.length > 0) {
          toast(warning, { duration: 5000 });
        } else if (!isDuplicateSkip) {
          toast(warning, { duration: 5000 });
        }
      }

      if (
        result.items.length > 0 &&
        result.invitation &&
        !result.invitation.failed &&
        (result.invitation.calendarForwarded || result.invitation.emailsSent)
      ) {
        toast.success(
          result.invitation.calendarForwarded
            ? 'Meeting invitation sent to newly added attendee(s).'
            : 'Meeting invitation email sent to newly added attendee(s).'
        );
      }
    },
  });

  const newMeetingInviteMutation = useMutation({
    mutationFn: () => sendNewCabMeetingInvite(meetingId!),
  });

  const handleAddAttendees = async (selected: LookupUser[]) => {
    if (selected.length === 0) return;
    const external = selected.filter((u) => !isAllowedCabEmail(u.email));
    if (external.length > 0) {
      toast.error(CAB_EMAIL_POLICY_MESSAGE);
      return;
    }
    try {
      await addAttendeesMutation.mutateAsync(selected);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Selected attendee(s) are already part of this meeting.');
    }
  };

  const updateMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formDataToUpdateMeetingPayload>) =>
      updateCabMeeting(meetingId!, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(['cab-meeting', meetingId], updated);
      queryClient.invalidateQueries({ queryKey: ['cab-meetings', updated.cabDefinitionId] });
      queryClient.invalidateQueries({ queryKey: ['cab-meeting-attendees', meetingId] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meeting', meetingId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCabMeeting(meetingId!),
    onSuccess: () => {
      if (record) {
        queryClient.invalidateQueries({ queryKey: ['cab-meetings', record.cabDefinitionId] });
      }
      queryClient.removeQueries({ queryKey: ['cab-meeting', meetingId] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
    },
  });

  const handleChange = (updates: Partial<CabMeetingFormData>) => {
    if (!formData) return;
    setFormData({ ...formData, ...updates });
    const clearedKeys = Object.keys(updates) as Array<keyof CabMeetingFormData>;
    setErrors((prev) => {
      const next = { ...prev };
      clearedKeys.forEach((key) => delete next[key]);
      if (clearedKeys.some((k) => k === 'meetingStart' || k === 'meetingEnd')) {
        delete next.dateTimeRange;
      }
      return next;
    });
  };

  const handleUpdate = async () => {
    if (!formData || !meetingId) return;

    const validationErrors = validateCabMeetingForm(formData);
    if (hasCabMeetingValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors before updating.');
      return;
    }

    try {
      const payload = formDataToUpdateMeetingPayload(formData);
      await updateMutation.mutateAsync(payload);
      toast.success('CAB Meeting updated successfully.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to update CAB Meeting. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!meetingId || !record) return;
    const confirmed = window.confirm('Are you sure you want to delete this CAB Meeting?');
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync();
      toast.success('CAB Meeting deleted.');
      navigate(
        `/admin/cab-meeting-management/cab-definitions/${record.cabDefinitionId}?tab=meetings`
      );
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to delete CAB Meeting. Please try again.');
    }
  };

  const handleNewMeetingInvite = async () => {
    if (!meetingId) return;
    const confirmed = window.confirm(
      'Send a meeting request to attendees with the latest meeting details and all Change Requests currently on this CAB Meeting agenda?'
    );
    if (!confirmed) return;

    try {
      const result = await newMeetingInviteMutation.mutateAsync();
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-attendees', meetingId] });
      toast.success(
        result.message ||
          'Meeting request sent successfully to the CAB Meeting attendees.'
      );
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to send the meeting request. Please try again later.');
    }
  };

  const isSaving =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    newMeetingInviteMutation.isPending;
  const apiErrorMessage =
    (error as { response?: { data?: { message?: string }; status?: number } })?.response?.data
      ?.message ||
    (error as Error)?.message ||
    'Failed to load CAB Meeting.';

  if (isLoading) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB Meeting</h1>
        <p className="text-red-600 mb-2">Unable to load this CAB Meeting.</p>
        <p className="text-sm text-gray-600 mb-4">{apiErrorMessage}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!record || !formData) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB Meeting</h1>
        <p className="text-gray-600 mb-4">Record not found.</p>
        <button
          type="button"
          onClick={() => navigate('/admin/cab-meeting-management/cab-definitions')}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back to CAB Definitions
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              CAB Meeting Management
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to={`/admin/cab-meeting-management/cab-definitions/${record.cabDefinitionId}?tab=meetings`}
              className="hover:text-indigo-600"
            >
              {record.cabDefinitionName}
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium truncate max-w-xs">{record.name}</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">CAB Meeting</h1>
          <p className="text-sm text-gray-600 mt-1">{record.name}</p>
        </div>

        <CabMeetingForm
          formData={formData}
          errors={errors}
          onChange={handleChange}
          contentTab={contentTab}
          onContentTabChange={setContentTab}
          onNewMeetingInvite={handleNewMeetingInvite}
          newMeetingInvitePending={newMeetingInviteMutation.isPending}
        />

        <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Updating...' : 'Update'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg mb-4">
        <div className="px-4 pt-4 pb-2 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-indigo-600">Related Links</h2>
        </div>
        <div className="px-4 py-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['cab-meeting-agenda', meetingId] });
              toast.success('Agenda Items refreshed.');
            }}
            className="text-left text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            Refresh Agenda Items
          </button>
          <button
            type="button"
            onClick={() => {
              void handleNewMeetingInvite();
            }}
            disabled={newMeetingInviteMutation.isPending}
            className="text-left text-sm text-indigo-600 hover:text-indigo-800 hover:underline disabled:opacity-50"
          >
            {newMeetingInviteMutation.isPending
              ? 'Sending...'
              : 'Send Meeting Request to Attendees'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg flex flex-col min-h-[320px]">
        <div className="border-b border-gray-200">
          <nav className="flex" aria-label="Related record tabs">
            <button
              type="button"
              onClick={() => setRelatedTab('agenda')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                relatedTab === 'agenda'
                  ? 'border-green-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Agenda Items
            </button>
            <button
              type="button"
              onClick={() => setRelatedTab('attendees')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                relatedTab === 'attendees'
                  ? 'border-green-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Attendees
            </button>
          </nav>
        </div>

        {relatedTab === 'agenda' ? (
          <div className="flex flex-col min-h-[280px]">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setAgendaSearch(agendaSearchInput.trim());
                  setAgendaPage(1);
                }}
                className="flex items-center gap-2"
              >
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={agendaSearchInput}
                    onChange={(e) => setAgendaSearchInput(e.target.value)}
                    placeholder="Search"
                    className="pl-8 pr-3 py-1.5 w-48 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Search
                </button>
              </form>
              <Link
                to={`/admin/cab-meeting-management/cab-meetings/${meetingId}/agenda-items/new`}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                New
              </Link>
            </div>
            <div className="flex-1 overflow-auto">
              {agendaLoading ? (
                <div className="p-8 text-center text-gray-500 text-sm">Loading agenda items...</div>
              ) : agendaItems.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p className="text-sm font-medium">No records to display.</p>
                  <p className="text-xs mt-1">Agenda items for this meeting will appear here.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {(
                        [
                          { key: 'sortOrder' as const, label: 'Order' },
                          { key: 'changeNumber' as const, label: 'Task' },
                          { key: 'shortDescription' as const, label: 'Short Description' },
                          { key: 'changeState' as const, label: 'State' },
                          { key: 'assignmentGroupName' as const, label: 'Assignment Group' },
                          { key: 'assignedToName' as const, label: 'Assigned To' },
                          { key: 'status' as const, label: 'Status' },
                        ] as const
                      ).map((col) => (
                        <th
                          key={col.key}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                        >
                          <button
                            type="button"
                            onClick={() => toggleAgendaSort(col.key)}
                            className="inline-flex items-center gap-1 hover:text-gray-800"
                          >
                            {col.label}
                            {agendaSortKey === col.key && (
                              <span aria-hidden>{agendaSortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedAgendaItems.map((item) => {
                      const editPath = `/admin/cab-meeting-management/cab-meetings/${meetingId}/agenda-items/${item.id}/edit`;
                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(editPath)}
                        >
                          <td className="px-4 py-2 text-sm text-gray-900">{item.sortOrder}</td>
                          <td className="px-4 py-2 text-sm">
                            <Link
                              to={editPath}
                              onClick={(e) => e.stopPropagation()}
                              className="text-indigo-700 font-medium hover:underline"
                            >
                              {item.changeNumber}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.shortDescription}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {formatAgendaChangeState(item.changeState)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {item.assignmentGroupName || '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {item.assignedToName || '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {getAgendaStatusLabel(item.status)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {agendaTotal > 0 && (
              <div className="px-4 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
                <span>
                  {(agendaPage - 1) * agendaPageSize + 1}–
                  {Math.min(agendaPage * agendaPageSize, agendaTotal)} of {agendaTotal}
                  {agendaFetching && !agendaLoading && (
                    <span className="ml-2 text-xs text-gray-400">Updating...</span>
                  )}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={agendaPage <= 1}
                    onClick={() => setAgendaPage((p) => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1">
                    Page {agendaPage} of {agendaTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={agendaPage >= agendaTotalPages}
                    onClick={() => setAgendaPage((p) => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col min-h-[280px]">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setAttendeeSearch(attendeeSearchInput.trim());
                  setAttendeePage(1);
                }}
                className="flex flex-wrap items-center gap-2"
              >
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={attendeeSearchInput}
                    onChange={(e) => setAttendeeSearchInput(e.target.value)}
                    placeholder="Search by name"
                    className="pl-8 pr-3 py-1.5 w-48 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <select
                  value={attendeeAttendanceFilter}
                  onChange={(e) => {
                    setAttendeeAttendanceFilter(e.target.value);
                    setAttendeePage(1);
                  }}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                  aria-label="Filter by attendance"
                >
                  <option value="">All attendance</option>
                  {CAB_ATTENDANCE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <select
                  value={attendeeReasonFilter}
                  onChange={(e) => {
                    setAttendeeReasonFilter(e.target.value);
                    setAttendeePage(1);
                  }}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                  aria-label="Filter by reason"
                >
                  <option value="">All reasons</option>
                  {CAB_ATTENDEE_REASON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Search
                </button>
              </form>
              <button
                type="button"
                onClick={() => setAttendeeLookupOpen(true)}
                disabled={addAttendeesMutation.isPending}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Attendee
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {attendeesLoading ? (
                <div className="p-8 text-center text-gray-500 text-sm">Loading attendees...</div>
              ) : attendees.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p className="text-sm font-medium">No records to display.</p>
                  <p className="text-xs mt-1">Attendees for this meeting will appear here.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {(
                        [
                          { key: 'attendeeName' as const, label: 'Attendee' },
                          { key: 'reason' as const, label: 'Reason' },
                          { key: 'attendanceStatus' as const, label: 'Attendance' },
                          { key: 'inviteSent' as const, label: 'Invite Sent' },
                        ] as const
                      ).map((col) => (
                        <th
                          key={col.key}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                        >
                          <button
                            type="button"
                            onClick={() => toggleAttendeeSort(col.key)}
                            className="inline-flex items-center gap-1 hover:text-gray-800"
                          >
                            {col.label}
                            {attendeeSortKey === col.key && (
                              <span aria-hidden>{attendeeSortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendees.map((item) => {
                      const editPath = `/admin/cab-meeting-management/cab-meetings/${meetingId}/attendees/${item.id}/edit`;
                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(editPath)}
                        >
                          <td className="px-4 py-2 text-sm">
                            <Link
                              to={editPath}
                              onClick={(e) => e.stopPropagation()}
                              className="text-indigo-700 font-medium hover:underline"
                            >
                              {item.attendeeName}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {getAttendeeReasonLabel(item.reason)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {getAttendanceStatusLabel(item.attendanceStatus)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.inviteSent ? 'True' : 'False'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {attendeesTotal > 0 && (
              <div className="px-4 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
                <span>
                  {(attendeePage - 1) * attendeePageSize + 1}–
                  {Math.min(attendeePage * attendeePageSize, attendeesTotal)} of {attendeesTotal}
                  {attendeesFetching && !attendeesLoading && (
                    <span className="ml-2 text-xs text-gray-400">Updating...</span>
                  )}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={attendeePage <= 1}
                    onClick={() => setAttendeePage((p) => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1">
                    Page {attendeePage} of {attendeesTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={attendeePage >= attendeesTotalPages}
                    onClick={() => setAttendeePage((p) => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <UserLookupModal
        isOpen={attendeeLookupOpen}
        onClose={() => setAttendeeLookupOpen(false)}
        mode="multi"
        title="Add Attendees"
        onConfirm={handleAddAttendees}
      />
    </div>
  );
};

export default CabMeetingDetail;
